import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CategoryIcon from '@mui/icons-material/Category';
import LogoutIcon from '@mui/icons-material/Logout';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { buildEventsApiUrl } from '../config/api';
import { getImageSourceLabel, normalizeImageUrl } from '../utils/images';
import ItemActivityModal from './ItemActivityModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface UserEvent {
  eventID: string;
  eventName: string;
  category: string;
  eventDateTime: string;
  tradeCutoffDate?: string;
  listingCityName: string;
  listingStateName: string;
  itemCount?: number;
  status: string;
  visibility?: 'draft' | 'active';
}

interface UserItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  condition?: string;
  images?: any[];
  createdAt?: string;
  createDateTime?: string;
  listedEvents?: { id: string; eventName: string; eventDateTime?: string | null }[];
  outgoingInterestCount?: number;
  incomingInterestCount?: number;
  outgoingInterests?: any[];
  incomingInterests?: any[];
  bggId?: number;
  bggName?: string;
  bggYearPublished?: number | null;
  bggThumbnail?: string;
  bggImage?: string;
  priceBand?: string;
  tags?: string[];
  status?: string;
  pendingTradeEventId?: string;
  pendingTradeEventName?: string;
  pendingTradeLockedAt?: string;
  tradedAt?: string;
  tradedEventId?: string;
  tradedEventName?: string;
  deletedAt?: string;
  bggMetadata?: {
    itemKind?: string;
  };
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}


// Helper function to calculate event status
function getEventStatus(eventDateTime: string, storedStatus: string): 'Past' | 'Active' | 'Draft' | 'Deleted' {
  if (storedStatus === 'deleted') return 'Deleted';
  if (storedStatus === 'draft') return 'Draft';
  
  const eventDate = new Date(eventDateTime);
  const now = new Date();
  
  if (eventDate < now) return 'Past';
  return 'Active';
}

// Helper function to get status color
function getStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'Active': return 'success';
    case 'Draft': return 'warning';
    case 'Past': return 'default';
    case 'Deleted': return 'error';
    default: return 'default';
  }
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user);
  const [authChecked, setAuthChecked] = useState(false);

  const getTabValueFromLocation = useCallback(() => {
    const tabParam = new URLSearchParams(location.search).get('tab');
    if (tabParam === 'joined-events') return 1;
    if (tabParam === 'my-items') return 2;
    return 0;
  }, [location.search]);

  const [tabValue, setTabValue] = useState(() => getTabValueFromLocation());

  const [userEvents, setUserEvents] = useState<UserEvent[]>([]);
  const [joinedEvents, setJoinedEvents] = useState<UserEvent[]>([]);
  const [userItems, setUserItems] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<UserItem | null>(null);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showTradedItems, setShowTradedItems] = useState(false);
  const [showDeletedItems, setShowDeletedItems] = useState(false);

  // Wait for auth to initialize
  useEffect(() => {
    const timer = setTimeout(() => {
      setAuthChecked(true);
      if (!isAuthenticated) {
        navigate('/');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, navigate]);

  const fetchUserData = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Fetching user data for:', userId);
      
      // Fetch user's events from the API (include deleted so the dashboard toggle can control visibility client-side)
      const eventsResponse = await fetch(buildEventsApiUrl(`/api/users/${userId}/events?includeDeleted=true`));
      if (!eventsResponse.ok) {
        console.warn('⚠️ Failed to fetch events:', eventsResponse.status);
        throw new Error(`Failed to fetch events: ${eventsResponse.status}`);
      }
      const eventsData = await eventsResponse.json();
      console.log('📋 Events response:', eventsData);
      
      // Transform events for display
      const displayEvents: UserEvent[] = (eventsData.events || [])
        .map((event: any) => {
          const calculatedStatus = getEventStatus(event.eventDateTime, event.status || 'active');
          return {
            eventID: event.eventID,
            eventName: event.eventName,
            category: event.category,
            eventDateTime: event.eventDateTime,
            tradeCutoffDate: event.tradeCutoffDate,
            listingCityName: event.listingCityName,
            listingStateName: event.listingStateName,
            itemCount: 0,
            status: calculatedStatus,
            visibility: (calculatedStatus === 'Active' ? 'active' : 'draft') as 'active' | 'draft'
          };
        });
      
      console.log(`✅ Loaded ${displayEvents.length} events`);
      
      // Fetch user's items from the API
      const itemsResponse = await fetch(buildEventsApiUrl(`/api/users/${userId}/items`));
      if (!itemsResponse.ok) {
        throw new Error('Failed to fetch items');
      }
      const itemsData = await itemsResponse.json();
      
      // Transform items for display
      const displayItems: UserItem[] = (itemsData.items || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        category: item.category,
        condition: item.condition || 'Excellent',
        description: item.description || '',
        images: item.images || [],
        createdAt: item.createdAt,
        createDateTime: item.createDateTime,
        listedEvents: item.listedEvents || [],
        outgoingInterestCount: item.outgoingInterestCount || 0,
        incomingInterestCount: item.incomingInterestCount || 0,
        outgoingInterests: item.outgoingInterests || [],
        incomingInterests: item.incomingInterests || [],
        bggId: item.bggId,
        bggName: item.bggName,
        bggYearPublished: item.bggYearPublished,
        bggThumbnail: item.bggThumbnail,
        bggImage: item.bggImage,
        priceBand: item.priceBand,
        tags: item.tags || [],
        status: item.status || 'available',
        pendingTradeEventId: item.pendingTradeEventId || '',
        pendingTradeEventName: item.pendingTradeEventName || '',
        pendingTradeLockedAt: item.pendingTradeLockedAt || '',
        tradedAt: item.tradedAt || '',
        tradedEventId: item.tradedEventId || '',
        tradedEventName: item.tradedEventName || '',
        deletedAt: item.deletedAt || '',
        bggMetadata: item.bggMetadata || {}
      }));
      
      console.log('📦 Dashboard item statuses:', displayItems.map((item) => ({ id: item.id, name: item.name, status: item.status })));
      setUserEvents(displayEvents);
      setUserItems(displayItems);
      
      // Fetch user's joined events
      const joinedResponse = await fetch(buildEventsApiUrl(`/api/users/${userId}/joined-events`));
      console.log('🔍 Joined events endpoint response status:', joinedResponse.status);
      if (!joinedResponse.ok) {
        console.error('❌ Failed to fetch joined events:', joinedResponse.status, joinedResponse.statusText);
        setJoinedEvents([]);
      } else {
        const joinedData = await joinedResponse.json();
        console.log('📋 Joined events response:', joinedData);
        console.log('📊 Events array:', joinedData.events || []);
        if (!Array.isArray(joinedData.events)) {
          console.error('❌ Events is not an array:', typeof joinedData.events, joinedData.events);
        }
        
        const displayJoined: UserEvent[] = (joinedData.events || [])
          .map((event: any) => {
            const calculatedStatus = getEventStatus(event.eventDateTime, event.status || 'active');
            return {
              eventID: event.eventID,
              eventName: event.eventName,
              category: event.category,
              eventDateTime: event.eventDateTime,
              tradeCutoffDate: event.tradeCutoffDate,
              listingCityName: event.listingCityName,
              listingStateName: event.listingStateName,
              itemCount: 0,
              status: calculatedStatus,
              visibility: (calculatedStatus === 'Active' ? 'active' : 'draft') as 'active' | 'draft'
            };
          });
        
        console.log(`✅ Loaded ${displayJoined.length} joined events`);
        setJoinedEvents(displayJoined);
      }
      
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      setError('Failed to load your data. Please try again.');
      setUserEvents([]);
      setUserItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);


  // Fetch data only after auth is checked
  useEffect(() => {
    if (authChecked && isAuthenticated) {
      fetchUserData();
    }
  }, [authChecked, isAuthenticated, fetchUserData]);

  useEffect(() => {
    setTabValue(getTabValueFromLocation());
  }, [getTabValueFromLocation]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const nextTab = newValue === 1 ? 'joined-events' : newValue === 2 ? 'my-items' : null;
    navigate(nextTab ? `/dashboard?tab=${nextTab}` : '/dashboard', { replace: true });
  };

  const handleEditItem = (itemId: string) => {
    navigate(`/edit-item/${itemId}`);
  };

  const visibleUserEvents = showPastEvents
    ? userEvents
    : userEvents.filter((event) => event.status !== 'Past');

  const visibleJoinedEvents = showPastEvents
    ? joinedEvents
    : joinedEvents.filter((event) => event.status !== 'Past');

  const handleEditEvent = (eventId: string) => {
    navigate(`/edit-event/${eventId}`);
  };

  const handleLeaveEvent = async (eventId: string) => {
    try {
      const response = await fetch(`http://localhost:4000/api/events/${eventId}/leave`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId })
      });

      if (response.ok) {
        setJoinedEvents(joinedEvents.filter(event => event.eventID !== eventId));
        console.log('✅ Left event:', eventId);
      } else {
        console.error('❌ Failed to leave event');
      }
    } catch (error) {
      console.error('❌ Error leaving event:', error);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      try {
        const response = await fetch(`http://localhost:4000/api/events/${eventId}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ownerID: userId })
        });

        if (response.ok) {
          setUserEvents(userEvents.filter(event => event.eventID !== eventId));
          setError(null);
        } else {
          setError('Failed to delete event');
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        setError('Failed to delete event');
      }
    }
  };

    const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const response = await fetch(buildEventsApiUrl(`/api/items/${itemId}`), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(userId ? { 'x-user-id': userId } : {})
          },
          body: JSON.stringify({ userId })
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok) {
          setUserItems((currentItems) => currentItems.filter(item => item.id !== itemId));
          setError(null);
        } else if (response.status === 409 && Array.isArray(data.blockingEvents) && data.blockingEvents.length > 0) {
          const eventNames = data.blockingEvents.map((event: any) => event.eventName || event.eventID || 'Unknown event').join(', ');
          const message = `Cannot delete this item because it is part of locked or cutoff-passed trade events: ${eventNames}`;
          setError(message);
          window.alert(message);
        } else {
          const message = data.error || `Failed to delete item (HTTP ${response.status})`;
          setError(message);
          window.alert(message);
        }
      } catch (error) {
        console.error('Error deleting item:', error);
        const message = error instanceof Error ? error.message : 'Failed to delete item';
        setError(message);
        window.alert(message);
      }
    }
  };

  // Show loading while auth initializes
  if (!authChecked) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const visibleUserItems = userItems.filter((item) => {
    if (item.status === 'deleted' && !showDeletedItems) return false;
    if (item.status === 'traded' && !showTradedItems) return false;
    return true;
  });

  console.log('🧮 Dashboard visible item statuses:', {
    total: userItems.map((item) => ({ name: item.name, status: item.status })),
    visible: visibleUserItems.map((item) => ({ name: item.name, status: item.status })),
    showTradedItems,
    showDeletedItems,
  });

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon /> My Dashboard
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="dashboard tabs"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minWidth: 0,
              px: { xs: 0.5, sm: 1.5 },
              py: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              textTransform: 'none'
            },
            '& .MuiTab-iconWrapper': {
              mr: { xs: 0.5, sm: 1 }
            }
          }}
        >
          <Tab label="My Events" data-testid="tab-my-events" {...a11yProps(0)} icon={<EventIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} iconPosition="start" />
          <Tab label="Joined Events" data-testid="tab-joined-events" {...a11yProps(1)} icon={<EventIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} iconPosition="start" />
          <Tab label="My Items" data-testid="tab-my-items" {...a11yProps(2)} icon={<InventoryIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* My Events Tab */}
      <TabPanel value={tabValue} index={0} data-testid="panel-my-events">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5">My Events</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPastEvents}
                  onChange={(e) => setShowPastEvents(e.target.checked)}
                />
              }
              label="Show past events"
              sx={{ mt: 1 }}
            />
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-event')}
          >
            Create Event
          </Button>
        </Box>

        {visibleUserEvents.length === 0 ? (
          <Alert severity="info">
            {showPastEvents
              ? 'You have no events to show right now.'
              : 'You have no current events to show. Turn on "Show past events" to see older ones.'}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {visibleUserEvents.map((event) => (
              <Grid item xs={12} md={6} key={event.eventID}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => navigate(`/events/${event.eventID}`)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: '600' }}>
                      {event.eventName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<CategoryIcon />}
                        label={event.category}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={event.status}
                        size="small"
                        color={getStatusColor(event.status)}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#666', fontSize: '0.9rem' }}>
                      <LocationOnIcon sx={{ fontSize: '1.1rem' }} />
                      {event.listingCityName}, {event.listingStateName}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.9rem' }}>
                      📅 Event: {new Date(event.eventDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Box>
                    {event.tradeCutoffDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.9rem' }}>
                        ⏰ Cutoff: {new Date(event.tradeCutoffDate).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent(event.eventID);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEvent(event.eventID);
                      }}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Joined Events Tab */}
      <TabPanel value={tabValue} index={1} data-testid="panel-joined-events">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5">Joined Events</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPastEvents}
                  onChange={(e) => setShowPastEvents(e.target.checked)}
                />
              }
              label="Show past events"
              sx={{ mt: 1 }}
            />
          </Box>
        </Box>

        {visibleJoinedEvents.length === 0 ? (
          <Alert severity="info">
            {showPastEvents
              ? 'You have no joined events to show right now.'
              : 'You have no current joined events to show. Turn on "Show past events" to see older ones.'}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {visibleJoinedEvents.map((event) => (
              <Grid item xs={12} md={6} key={event.eventID}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => navigate(`/events/${event.eventID}`)}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: '600' }}>
                      {event.eventName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<CategoryIcon />}
                        label={event.category}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={event.status}
                        size="small"
                        color={getStatusColor(event.status)}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#666', fontSize: '0.9rem' }}>
                      <LocationOnIcon sx={{ fontSize: '1.1rem' }} />
                      {event.listingCityName}, {event.listingStateName}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.9rem' }}>
                      📅 Event: {new Date(event.eventDateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </Box>
                    {event.tradeCutoffDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#666', fontSize: '0.9rem' }}>
                        ⏰ Cutoff: {new Date(event.tradeCutoffDate).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      color="error"
                      startIcon={<LogoutIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLeaveEvent(event.eventID);
                      }}
                    >
                      Leave Event
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

            {/* My Items Tab */}
      <TabPanel value={tabValue} index={2} data-testid="panel-my-items">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5">My Items</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={showTradedItems} onChange={(e) => setShowTradedItems(e.target.checked)} />}
              label="Show traded"
            />
            <FormControlLabel
              control={<Checkbox checked={showDeletedItems} onChange={(e) => setShowDeletedItems(e.target.checked)} />}
              label="Show deleted"
            />
            <Button
              variant="outlined"
              onClick={() => navigate('/inventory/import/bgg')}
            >
              Import from BoardGameGeek
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => navigate('/create-item')}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        {visibleUserItems.length === 0 ? (
          <Alert severity="info">
            You haven't added any items yet. Click "Add Item" to get started!
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {visibleUserItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {(item.images && item.images.length > 0) || item.bggThumbnail || item.bggImage ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={normalizeImageUrl(item.images?.[0] || item.bggImage || item.bggThumbnail)}
                        alt={item.name}
                        sx={{ width: '100%', height: 200, objectFit: 'cover', backgroundColor: '#f0f0f0' }}
                      />
                      <Chip
                        label={getImageSourceLabel(item.images?.[0], Boolean(item.bggId) && !(item.images?.[0] as any)?.source)}
                        size="small"
                        sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'background.paper' }}
                      />
                    </Box>
                  ) : null}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: '600' }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {item.status === 'trade-pending' && (
                        <Chip label={`Trade Pending${item.pendingTradeEventName ? `: ${item.pendingTradeEventName}` : ''}`} size="small" color="warning" />
                      )}
                      {item.status === 'traded' && (
                        <Chip label={`Traded${item.tradedEventName ? `: ${item.tradedEventName}` : ''}`} size="small" color="default" />
                      )}
                      {item.status === 'deleted' && (
                        <Chip label="Deleted" size="small" color="error" />
                      )}
                      <Chip 
                        icon={<CategoryIcon />}
                        label={item.category}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={item.condition}
                        size="small"
                        variant="outlined"
                      />
                      {!!item.bggId && (
                        <Chip label={`BGG #${item.bggId}`} size="small" color="primary" variant="outlined" />
                      )}
                      {!!item.bggMetadata?.itemKind && (
                        <Chip label={item.bggMetadata.itemKind} size="small" variant="outlined" />
                      )}
                      {!!item.priceBand && (
                        <Chip
                          label={`Estimated value $${item.priceBand}`}
                          size="small"
                          color="success"
                          sx={{
                            fontWeight: 700,
                            '& .MuiChip-label': {
                              px: 1.25
                            }
                          }}
                        />
                      )}
                      {!!item.bggYearPublished && (
                        <Chip label={`Year ${item.bggYearPublished}`} size="small" variant="outlined" />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      {item.description}
                    </Typography>
                    {item.status === 'trade-pending' && item.pendingTradeLockedAt && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#8a6d3b', mb: 1 }}>
                        Pending since {new Date(item.pendingTradeLockedAt).toLocaleString()}
                      </Typography>
                    )}
                    {item.status === 'traded' && item.tradedAt && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                        Traded on {new Date(item.tradedAt).toLocaleString()}
                      </Typography>
                    )}
                    {item.status === 'deleted' && item.deletedAt && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                        Deleted on {new Date(item.deletedAt).toLocaleString()}
                      </Typography>
                    )}
                    {!!item.bggName && item.bggName !== item.name && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', mb: 1 }}>
                        Imported from BGG as: {item.bggName}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`Events: ${(item.listedEvents || []).length}`} />
                      <Chip size="small" label={`Offers Made: ${item.outgoingInterestCount || 0}`} />
                      <Chip size="small" label={`Offers Received: ${item.incomingInterestCount || 0}`} />
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedItem(item)}
                    >
                      Activity
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditItem(item.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      Delete
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <ItemActivityModal
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </Container>
  );
};

export default UserDashboard;














